import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions } from '../firebase';
import { Suggestion } from '../types/firestore';
import { isSuggestionArchived } from '../utils/suggestionArchive';
import {
  normalizeSuggestionStatus,
  normalizeSuggestionWorkflowVersion,
} from '../utils/suggestionWorkflow';

interface SubmitSuggestionV2Input {
  originalTitle: string;
  originalDescription: string;
}

interface SubmitSuggestionV2Response {
  success: boolean;
  suggestionId: string;
  workflowVersion?: number;
  status?: string;
}

interface SaveSuggestionTechnicalVersionInput {
  suggestionId: string;
  technicalTitle: string;
  technicalSummary: string;
}

interface DeclineSuggestionByAdminInput {
  suggestionId: string;
  reason: string;
}

interface OpenSuggestionVotingInput {
  suggestionId: string;
}

interface CastSuggestionVoteInput {
  suggestionId: string;
  vote: 'yes' | 'no';
}

interface CloseSuggestionVotingIfEligibleInput {
  suggestionId: string;
}

interface SetSuggestionImplementationStateInput {
  suggestionId: string;
  nextStatus: 'planned' | 'in_progress' | 'implemented';
}

interface ArchiveRestoreDeleteInput {
  suggestionId: string;
  confirmToken?: string;
}

interface ModerateLegacySuggestionInput {
  suggestionId: string;
  status?: 'accepted' | 'rejected';
  responseText?: string;
}

interface SuggestionParticipation {
  myVote: Suggestion['myVote'];
  eligibleToVote: boolean;
  voteUpdatedAt: Suggestion['myVoteUpdatedAt'] | null;
}

interface SuggestionFeedOptions {
  includeMyVotes?: boolean;
  currentUserId?: string | null;
  includeArchived?: boolean;
}

const SUGGESTIONS_COLLECTION = 'suggestions';
const VOTES_SUBCOLLECTION = 'votes';
const ELECTORATE_SUBCOLLECTION = 'votingElectorate';

const asTrimmedString = (value: unknown): string =>
  typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : '';

const asOptionalTrimmedString = (value: unknown): string | null => {
  const normalized = asTrimmedString(value);
  return normalized || null;
};

const asNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  return null;
};

const normalizeVoteValue = (value: unknown): Suggestion['myVote'] => {
  const normalized = asTrimmedString(value).toLowerCase();
  if (normalized === 'yes' || normalized === 'no') {
    return normalized;
  }
  return null;
};

const getSuggestionVoteDocRef = (suggestionId: string, userId: string) =>
  doc(firestore, SUGGESTIONS_COLLECTION, suggestionId, VOTES_SUBCOLLECTION, userId);

const getSuggestionIdentity = (suggestion: Suggestion): string =>
  suggestion.id || suggestion.suggestionId || suggestion.uid;

const readVoteValue = (voteSnapshot: {
  exists: () => boolean;
  data: () => DocumentData | undefined;
}) => {
  if (!voteSnapshot.exists()) {
    return null;
  }

  const voteData = (voteSnapshot.data() || {}) as Record<string, unknown>;
  return normalizeVoteValue(voteData.vote || voteData.voteValue);
};

const loadMyVotesForSuggestions = async (
  suggestionIds: string[],
  userId: string
): Promise<Map<string, Suggestion['myVote']>> => {
  const normalizedUserId = asTrimmedString(userId);
  const normalizedSuggestionIds = Array.from(
    new Set(suggestionIds.map((suggestionId) => asTrimmedString(suggestionId)).filter(Boolean))
  );

  if (!normalizedUserId || normalizedSuggestionIds.length === 0) {
    return new Map();
  }

  const voteEntries = await Promise.all(
    normalizedSuggestionIds.map(async (suggestionId) => {
      const voteSnapshot = await getDoc(getSuggestionVoteDocRef(suggestionId, normalizedUserId));
      return [suggestionId, readVoteValue(voteSnapshot)] as const;
    })
  );

  return new Map(
    voteEntries.filter(([, vote]) => vote === 'yes' || vote === 'no') as Array<
      readonly [string, Suggestion['myVote']]
    >
  );
};

const mapSuggestionDoc = (
  docSnap: QueryDocumentSnapshot<DocumentData>,
  myVotes?: Map<string, Suggestion['myVote']>
): Suggestion => {
  const data = docSnap.data() as Record<string, unknown>;
  const workflowVersion = normalizeSuggestionWorkflowVersion(data.workflowVersion);
  const status = normalizeSuggestionStatus(data.status);
  const originalTitle =
    asTrimmedString(data.originalTitle) ||
    asTrimmedString(data.title) ||
    asTrimmedString(data.technicalTitle) ||
    'Bez tytułu';
  const originalDescription =
    asTrimmedString(data.originalDescription) ||
    asTrimmedString(data.description) ||
    asTrimmedString(data.technicalSummary) ||
    'Brak opisu.';
  const technicalTitle = asOptionalTrimmedString(data.technicalTitle);
  const technicalSummary = asOptionalTrimmedString(data.technicalSummary);
  const authorUserId = asTrimmedString(data.createdBy || data.userId) || docSnap.id;
  const authorDisplayName = asOptionalTrimmedString(
    data.createdByDisplayName || data.userDisplayName
  );
  const createdAt = data.createdAt as Suggestion['createdAt'];
  const updatedAt = data.updatedAt as Suggestion['updatedAt'];
  const result = data.result === 'accepted' || data.result === 'rejected' ? data.result : null;

  const response = data.response as
    | {
        text?: unknown;
        createdAt?: unknown;
      }
    | undefined;
  const responseText = response?.text;
  const responseCreatedAt = response?.createdAt;

  return {
    uid: docSnap.id,
    id: docSnap.id,
    suggestionId: docSnap.id,
    workflowVersion,
    userId: authorUserId,
    userDisplayName: authorDisplayName || undefined,
    createdBy: authorUserId,
    createdByDisplayName: authorDisplayName,
    title: originalTitle,
    description: originalDescription,
    originalTitle,
    originalDescription,
    technicalTitle,
    technicalSummary,
    technicalEditedBy: asOptionalTrimmedString(data.technicalEditedBy),
    technicalEditedByDisplayName: asOptionalTrimmedString(data.technicalEditedByDisplayName),
    technicalEditedAt: (data.technicalEditedAt as Suggestion['technicalEditedAt']) || null,
    category: asOptionalTrimmedString(data.category) || undefined,
    isArchived: isSuggestionArchived({ isArchived: data.isArchived, status }),
    status,
    createdAt,
    updatedAt,
    votingOpenedAt: (data.votingOpenedAt as Suggestion['votingOpenedAt']) || null,
    votingClosesAt: (data.votingClosesAt as Suggestion['votingClosesAt']) || null,
    votingClosedAt: (data.votingClosedAt as Suggestion['votingClosedAt']) || null,
    votingClosedBy: asOptionalTrimmedString(data.votingClosedBy),
    eligibleVoterCountSnapshot: asNumber(data.eligibleVoterCountSnapshot) || 0,
    yesCount: asNumber(data.yesCount) || 0,
    noCount: asNumber(data.noCount) || 0,
    turnoutCount: asNumber(data.turnoutCount) || 0,
    result,
    resolutionReason: asOptionalTrimmedString(data.resolutionReason),
    adminDecisionReason: asOptionalTrimmedString(data.adminDecisionReason),
    archivedFromStatus: asOptionalTrimmedString(data.archivedFromStatus),
    archivedAt: (data.archivedAt as Suggestion['archivedAt']) || null,
    archivedBy: asOptionalTrimmedString(data.archivedBy),
    myVote: myVotes?.get(docSnap.id) || null,
    myVoteUpdatedAt: (data.myVoteUpdatedAt as Suggestion['myVoteUpdatedAt']) || null,
    response: responseText
      ? {
          text: asTrimmedString(responseText),
          createdAt: responseCreatedAt as Suggestion['createdAt'],
        }
      : undefined,
  };
};

const callSuggestionFunction = async <TInput, TOutput>(
  name: string,
  payload: TInput
): Promise<TOutput> => {
  const callable = httpsCallable<TInput, TOutput>(functions, name);
  const result = await callable(payload);
  return result.data;
};

export const submitSuggestionV2 = async (
  input: SubmitSuggestionV2Input
): Promise<SubmitSuggestionV2Response> => {
  const response = await callSuggestionFunction<
    SubmitSuggestionV2Input,
    SubmitSuggestionV2Response
  >('submitSuggestionV2', {
    originalTitle: asTrimmedString(input.originalTitle),
    originalDescription: asTrimmedString(input.originalDescription),
  });

  if (!response?.suggestionId) {
    throw new Error('Backend nie zwrócił identyfikatora sugestii.');
  }

  return response;
};

export const saveSuggestionTechnicalVersion = async (
  input: SaveSuggestionTechnicalVersionInput
): Promise<SubmitSuggestionV2Response> =>
  callSuggestionFunction<SaveSuggestionTechnicalVersionInput, SubmitSuggestionV2Response>(
    'saveSuggestionTechnicalVersion',
    {
      suggestionId: asTrimmedString(input.suggestionId),
      technicalTitle: asTrimmedString(input.technicalTitle),
      technicalSummary: asTrimmedString(input.technicalSummary),
    }
  );

export const declineSuggestionByAdmin = async (
  input: DeclineSuggestionByAdminInput
): Promise<SubmitSuggestionV2Response> =>
  callSuggestionFunction<DeclineSuggestionByAdminInput, SubmitSuggestionV2Response>(
    'declineSuggestionByAdmin',
    {
      suggestionId: asTrimmedString(input.suggestionId),
      reason: asTrimmedString(input.reason),
    }
  );

export const openSuggestionVoting = async (
  input: OpenSuggestionVotingInput
): Promise<SubmitSuggestionV2Response> =>
  callSuggestionFunction<OpenSuggestionVotingInput, SubmitSuggestionV2Response>(
    'openSuggestionVoting',
    {
      suggestionId: asTrimmedString(input.suggestionId),
    }
  );

export const castSuggestionVote = async (
  input: CastSuggestionVoteInput
): Promise<SubmitSuggestionV2Response> =>
  callSuggestionFunction<CastSuggestionVoteInput, SubmitSuggestionV2Response>(
    'castSuggestionVote',
    {
      suggestionId: asTrimmedString(input.suggestionId),
      vote: input.vote,
    }
  );

export const closeSuggestionVotingIfEligible = async (
  input: CloseSuggestionVotingIfEligibleInput
): Promise<SubmitSuggestionV2Response> =>
  callSuggestionFunction<CloseSuggestionVotingIfEligibleInput, SubmitSuggestionV2Response>(
    'closeSuggestionVotingIfEligible',
    {
      suggestionId: asTrimmedString(input.suggestionId),
    }
  );

export const setSuggestionImplementationState = async (
  input: SetSuggestionImplementationStateInput
): Promise<SubmitSuggestionV2Response> =>
  callSuggestionFunction<SetSuggestionImplementationStateInput, SubmitSuggestionV2Response>(
    'setSuggestionImplementationState',
    {
      suggestionId: asTrimmedString(input.suggestionId),
      nextStatus: input.nextStatus,
    }
  );

export const archiveSuggestion = async (
  input: ArchiveRestoreDeleteInput
): Promise<SubmitSuggestionV2Response> =>
  callSuggestionFunction<ArchiveRestoreDeleteInput, SubmitSuggestionV2Response>(
    'archiveSuggestion',
    {
      suggestionId: asTrimmedString(input.suggestionId),
    }
  );

export const restoreSuggestion = async (
  input: ArchiveRestoreDeleteInput
): Promise<SubmitSuggestionV2Response> =>
  callSuggestionFunction<ArchiveRestoreDeleteInput, SubmitSuggestionV2Response>(
    'restoreSuggestion',
    {
      suggestionId: asTrimmedString(input.suggestionId),
    }
  );

export const deleteSuggestionHard = async (
  input: ArchiveRestoreDeleteInput
): Promise<SubmitSuggestionV2Response> =>
  callSuggestionFunction<ArchiveRestoreDeleteInput, SubmitSuggestionV2Response>(
    'deleteSuggestionHard',
    {
      suggestionId: asTrimmedString(input.suggestionId),
      confirmToken: asOptionalTrimmedString(input.confirmToken) || undefined,
    }
  );

export const moderateLegacySuggestion = async (
  input: ModerateLegacySuggestionInput
): Promise<SubmitSuggestionV2Response> =>
  callSuggestionFunction<ModerateLegacySuggestionInput, SubmitSuggestionV2Response>(
    'moderateLegacySuggestion',
    {
      suggestionId: asTrimmedString(input.suggestionId),
      status: input.status,
      responseText: asOptionalTrimmedString(input.responseText) || undefined,
    }
  );

export const getSuggestionsFeed = async (
  options: SuggestionFeedOptions = {}
): Promise<Suggestion[]> => {
  const suggestionsQuery = query(
    collection(firestore, SUGGESTIONS_COLLECTION),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(suggestionsQuery);

  const suggestions = snapshot.docs.map((docSnap) => mapSuggestionDoc(docSnap));
  const visibleSuggestions =
    options.includeArchived === false
      ? suggestions.filter((item) => !isSuggestionArchived(item))
      : suggestions;

  if (!options.includeMyVotes || !options.currentUserId) {
    return visibleSuggestions;
  }

  const myVotes = await loadMyVotesForSuggestions(
    visibleSuggestions.map((suggestion) => getSuggestionIdentity(suggestion)),
    options.currentUserId
  );

  return visibleSuggestions.map((suggestion) => ({
    ...suggestion,
    myVote: myVotes.get(getSuggestionIdentity(suggestion)) || null,
  }));
};

export const getSuggestionById = async (
  suggestionId: string,
  options: { currentUserId?: string | null; includeMyVotes?: boolean } = {}
): Promise<Suggestion | null> => {
  const normalizedSuggestionId = asTrimmedString(suggestionId);
  if (!normalizedSuggestionId) {
    return null;
  }

  const suggestionSnap = await getDoc(
    doc(firestore, SUGGESTIONS_COLLECTION, normalizedSuggestionId)
  );
  if (!suggestionSnap.exists()) {
    return null;
  }

  const suggestion = mapSuggestionDoc(suggestionSnap);
  if (!options.includeMyVotes || !options.currentUserId) {
    return suggestion;
  }

  const voteSnapshot = await getDoc(
    getSuggestionVoteDocRef(normalizedSuggestionId, options.currentUserId)
  );

  return {
    ...suggestion,
    myVote: readVoteValue(voteSnapshot),
  };
};

export const getSuggestionParticipation = async (
  suggestionId: string,
  userId: string
): Promise<SuggestionParticipation> => {
  const normalizedSuggestionId = asTrimmedString(suggestionId);
  const normalizedUserId = asTrimmedString(userId);
  if (!normalizedSuggestionId || !normalizedUserId) {
    return {
      myVote: null,
      eligibleToVote: false,
      voteUpdatedAt: null,
    };
  }

  const voteDocRef = getSuggestionVoteDocRef(normalizedSuggestionId, normalizedUserId);
  const electorateDocRef = doc(
    firestore,
    SUGGESTIONS_COLLECTION,
    normalizedSuggestionId,
    ELECTORATE_SUBCOLLECTION,
    normalizedUserId
  );

  const [voteSnapshot, electorateSnap] = await Promise.all([
    getDoc(voteDocRef),
    getDoc(electorateDocRef),
  ]);
  const voteData = voteSnapshot.exists() ? (voteSnapshot.data() as Record<string, unknown>) : {};
  const electorateData = electorateSnap.exists()
    ? (electorateSnap.data() as Record<string, unknown>)
    : {};

  return {
    myVote: normalizeVoteValue(voteData.vote || voteData.voteValue),
    eligibleToVote: electorateSnap.exists() && electorateData.eligibleAtSnapshot === true,
    voteUpdatedAt:
      (voteData.voteUpdatedAt as Suggestion['myVoteUpdatedAt']) ||
      (voteData.updatedAt as Suggestion['myVoteUpdatedAt']) ||
      null,
  };
};

export const addSuggestion = async (
  suggestionData: Omit<Suggestion, 'id' | 'status' | 'createdAt'>
): Promise<string> => {
  const { userId, title, description } = suggestionData;
  if (!userId || !title || !description) {
    throw new Error('addSuggestion: Brakuje wymaganych danych (userId, title, description).');
  }

  const result = await submitSuggestionV2({
    originalTitle: title,
    originalDescription: description,
  });

  return result.suggestionId;
};

export const getAllSuggestions = async (): Promise<Suggestion[]> => getSuggestionsFeed();

export const getActiveSuggestions = async (): Promise<Suggestion[]> =>
  (await getSuggestionsFeed()).filter((item) => !isSuggestionArchived(item));

export const getArchivedSuggestions = async (): Promise<Suggestion[]> =>
  (await getSuggestionsFeed()).filter((item) => isSuggestionArchived(item));

export const getSuggestionsForAdmin = async (): Promise<{
  active: Suggestion[];
  archived: Suggestion[];
}> => {
  const suggestions = await getSuggestionsFeed();
  return {
    active: suggestions.filter((item) => !isSuggestionArchived(item)),
    archived: suggestions.filter((item) => isSuggestionArchived(item)),
  };
};

export const updateSuggestionByAdmin = async (
  suggestionId: string,
  options: {
    status?: Suggestion['status'];
    responseText?: string;
  }
): Promise<void> => {
  const status = asTrimmedString(options.status).toLowerCase();
  const responseText = asOptionalTrimmedString(options.responseText);

  if (!responseText && status !== 'accepted' && status !== 'rejected') {
    throw new Error('updateSuggestionByAdmin: Brak danych do aktualizacji.');
  }

  if (responseText && status !== 'accepted' && status !== 'rejected') {
    await moderateLegacySuggestion({
      suggestionId,
      responseText,
    });
    return;
  }

  await moderateLegacySuggestion({
    suggestionId,
    status: status === 'accepted' ? 'accepted' : 'rejected',
    responseText: responseText || undefined,
  });
};

export const setSuggestionArchivedByAdmin = async (
  suggestionId: string,
  options: {
    archived: boolean;
    actorUserId?: string | null;
  }
): Promise<void> => {
  if (options.archived) {
    await archiveSuggestion({ suggestionId });
    return;
  }

  await restoreSuggestion({ suggestionId });
};

export const deleteSuggestionByAdmin = async (suggestionId: string): Promise<void> => {
  await deleteSuggestionHard({
    suggestionId,
    confirmToken: 'USUN',
  });
};

export const suggestionService = {
  submitSuggestionV2,
  saveSuggestionTechnicalVersion,
  declineSuggestionByAdmin,
  openSuggestionVoting,
  castSuggestionVote,
  closeSuggestionVotingIfEligible,
  setSuggestionImplementationState,
  archiveSuggestion,
  restoreSuggestion,
  deleteSuggestionHard,
  moderateLegacySuggestion,
  getSuggestionById,
  getSuggestionParticipation,
  getSuggestionsFeed,
  addSuggestion,
  getAllSuggestions,
  getActiveSuggestions,
  getArchivedSuggestions,
  getSuggestionsForAdmin,
  updateSuggestionByAdmin,
  setSuggestionArchivedByAdmin,
  deleteSuggestionByAdmin,
};
